/**
 * แคชแถว ledger สต๊อกในเครื่อง (IndexedDB) — ใช้เฉพาะ local mode
 *
 * - หนึ่งร้าน = หนึ่ง database (`xtable-stock:{storeId}`) — เครื่องกลางหน้าร้านมีหลายบัญชีร้านสลับกันใช้
 *   แต่ละร้านมีแคชของตัวเอง ไม่ปนกัน, logout ไม่ลบ, กลับมาร้านเดิมใช้แคชเดิมต่อได้
 * - db หนึ่งตัวผูกกับร้านเดียว เรียกด้วย storeId อื่น = throw (กันบั๊กเขียนข้ามร้าน)
 * - ใน database: object store `rows` keyPath key2, `meta` keyPath storeId (complete / checkpoint / อายุแคช)
 * - ทุกการ commit ที่แก้ทั้งแถวและ meta ทำใน transaction เดียว (แท็บปิดกลางทาง = ไม่มีอะไรเปลี่ยน)
 * - commitChanges เป็น compare-and-set บน checkpoint: งาน sync เก่าที่ค้างอยู่จะ commit ทับงานใหม่ไม่ได้
 * - แท็บอื่นลบ database นี้ (flag ถูกปิด) -> ปิด connection ให้ แล้วเปิดใหม่เองในคำสั่งถัดไป (ได้ db ว่าง)
 * - เปิด IndexedDB ไม่ได้ (private mode / ถูกปิด) -> ใช้หน่วยความจำแทน ทำงานได้แต่ไม่จำข้ามการเปิดแอป
 *
 * ไฟล์นี้ไม่ import อะไร (เทสต์ใน node ใช้ memory store ได้ตรง ๆ)
 */

import type { StockCacheMeta, StockRow } from "./stockCheckpoint";

export interface CommitChangesInput {
  rows: StockRow[];
  deleted: string[];
  /** checkpoint ที่งานนี้เริ่มจาก  ถ้าในเครื่องไม่ตรงแล้ว = มีงานอื่นเขียนไปก่อน -> ยกเลิก */
  expectedCheckpoint: number;
  meta: StockCacheMeta;
}

export interface StockDb {
  readonly kind: "indexeddb" | "memory";
  /** ร้านที่ db นี้เป็นของ — ทุกคำสั่งต้องส่ง storeId ตรงกัน */
  readonly storeId: string;
  load(storeId: string): Promise<{ meta: StockCacheMeta | null; rows: StockRow[] }>;
  readMeta(storeId: string): Promise<StockCacheMeta | null>;
  commitFullLoad(storeId: string, rows: StockRow[], meta: StockCacheMeta): Promise<void>;
  /** false = checkpoint ในเครื่องไม่ใช่ค่าที่คาด ไม่ได้เขียนอะไรเลย */
  commitChanges(storeId: string, input: CommitChangesInput): Promise<boolean>;
  putRow(storeId: string, row: StockRow): Promise<void>;
  /** ลบแถว + meta ของร้านนี้ (ปุ่ม Reload) */
  clearStore(storeId: string): Promise<void>;
  /** ปิด connection (ก่อนลบ database) */
  close(): void;
}

const DB_PREFIX = "xtable-stock:";
const DB_VERSION = 1;
const ROWS = "rows";
const META = "meta";

export const stockDbName = (storeId: string) => `${DB_PREFIX}${storeId}`;

const assertStore = (owner: string, storeId: string) => {
  if (!owner || storeId !== owner) {
    throw new Error(`stock db: db ของร้าน "${owner}" ถูกเรียกด้วยร้าน "${storeId}"`);
  }
};

const withKey = (storeId: string, row: StockRow): StockRow => ({
  ...row,
  storeId,
  key2: row.key2 || `${row.businessDate}:${row.createdAt}`,
});

// ---------------------------------------------------------------------------
// memory
// ---------------------------------------------------------------------------

export const createMemoryDb = (owner: string): StockDb => {
  if (!owner) throw new Error("stock db: ไม่มี storeId");
  let meta: StockCacheMeta | null = null;
  let rows = new Map<string, StockRow>();
  const copy = <T>(value: T): T => JSON.parse(JSON.stringify(value));

  return {
    kind: "memory",
    storeId: owner,
    async load(storeId) {
      assertStore(owner, storeId);
      return { meta: meta ? copy(meta) : null, rows: [...rows.values()].map(copy) };
    },
    async readMeta(storeId) {
      assertStore(owner, storeId);
      return meta ? copy(meta) : null;
    },
    async commitFullLoad(storeId, list, nextMeta) {
      assertStore(owner, storeId);
      rows = new Map(list.map((row) => [withKey(storeId, row).key2!, copy(withKey(storeId, row))]));
      meta = copy(nextMeta);
    },
    async commitChanges(storeId, { rows: changed, deleted, expectedCheckpoint, meta: nextMeta }) {
      assertStore(owner, storeId);
      if (!meta || !meta.complete || meta.checkpoint !== expectedCheckpoint) return false;
      for (const key of deleted) rows.delete(key);
      for (const row of changed) {
        const stored = withKey(storeId, row);
        rows.set(stored.key2!, copy(stored));
      }
      meta = copy(nextMeta);
      return true;
    },
    async putRow(storeId, row) {
      assertStore(owner, storeId);
      const stored = withKey(storeId, row);
      rows.set(stored.key2!, copy(stored));
    },
    async clearStore(storeId) {
      assertStore(owner, storeId);
      rows = new Map();
      meta = null;
    },
    close() {},
  };
};

// ---------------------------------------------------------------------------
// IndexedDB
// ---------------------------------------------------------------------------

const hasIndexedDb = () => {
  try {
    return typeof indexedDB !== "undefined" && !!indexedDB;
  } catch {
    return false;
  }
};

const requestToPromise = <T>(request: IDBRequest<T>): Promise<T> =>
  new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });

const transactionDone = (tx: IDBTransaction): Promise<void> =>
  new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error || new Error("transaction aborted"));
  });

const openIndexedDb = (storeId: string): Promise<IDBDatabase | null> =>
  new Promise((resolve) => {
    try {
      if (!hasIndexedDb()) return resolve(null);
      const request = indexedDB.open(stockDbName(storeId), DB_VERSION);
      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(ROWS)) db.createObjectStore(ROWS, { keyPath: "key2" });
        if (!db.objectStoreNames.contains(META)) db.createObjectStore(META, { keyPath: "storeId" });
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => resolve(null);
      request.onblocked = () => resolve(null);
    } catch {
      resolve(null);
    }
  });

const createIdbDb = (owner: string, first: IDBDatabase): StockDb => {
  let connection: Promise<IDBDatabase | null> | null = null;
  // close() จากเครื่องนี้ (ก่อนลบ database) = ปิดถาวร งานเก่าที่ค้างจะไม่เปิด database ที่เพิ่งลบขึ้นมาใหม่
  let closed = false;

  // แท็บอื่นสั่งลบ database นี้ -> ปิดให้เขาลบได้ คำสั่งถัดไปเปิดใหม่ (ได้ db ว่าง แล้ว engine จะโหลดเต็มเอง)
  const watch = (db: IDBDatabase) => {
    db.onversionchange = () => {
      db.close();
      connection = null;
    };
    db.onclose = () => {
      connection = null;
    };
    return db;
  };
  connection = Promise.resolve(watch(first));

  const conn = async (): Promise<IDBDatabase> => {
    if (closed) throw new Error("stock db: ปิดแล้ว");
    if (!connection) connection = openIndexedDb(owner).then((db) => (db ? watch(db) : null));
    const db = await connection;
    if (!db) {
      connection = null;
      throw new Error("stock db: เปิด IndexedDB ไม่ได้");
    }
    return db;
  };

  return {
    kind: "indexeddb",
    storeId: owner,

    async load(storeId) {
      assertStore(owner, storeId);
      const tx = (await conn()).transaction([ROWS, META], "readonly");
      const [rows, meta] = await Promise.all([
        requestToPromise(tx.objectStore(ROWS).getAll()),
        requestToPromise(tx.objectStore(META).get(storeId)),
      ]);
      return { rows: (rows as StockRow[]) || [], meta: (meta as StockCacheMeta) || null };
    },

    async readMeta(storeId) {
      assertStore(owner, storeId);
      const tx = (await conn()).transaction(META, "readonly");
      return ((await requestToPromise(tx.objectStore(META).get(storeId))) as StockCacheMeta) || null;
    },

    async commitFullLoad(storeId, rows, meta) {
      assertStore(owner, storeId);
      const tx = (await conn()).transaction([ROWS, META], "readwrite");
      const done = transactionDone(tx);
      const rowStore = tx.objectStore(ROWS);
      rowStore.clear();
      for (const row of rows) rowStore.put(withKey(storeId, row));
      tx.objectStore(META).put(meta);
      await done;
    },

    async commitChanges(storeId, { rows, deleted, expectedCheckpoint, meta }) {
      assertStore(owner, storeId);
      const tx = (await conn()).transaction([ROWS, META], "readwrite");
      const done = transactionDone(tx);
      let applied = false;

      const metaRequest = tx.objectStore(META).get(storeId);
      metaRequest.onsuccess = () => {
        const current = metaRequest.result as StockCacheMeta | undefined;
        if (!current || !current.complete || current.checkpoint !== expectedCheckpoint) {
          tx.abort();
          return;
        }
        const rowStore = tx.objectStore(ROWS);
        for (const key of deleted) rowStore.delete(key);
        for (const row of rows) rowStore.put(withKey(storeId, row));
        tx.objectStore(META).put(meta);
        applied = true;
      };

      try {
        await done;
        return applied;
      } catch (error) {
        if (!applied) return false; // abort ตั้งใจ = checkpoint ไม่ตรง
        throw error;
      }
    },

    async putRow(storeId, row) {
      assertStore(owner, storeId);
      const tx = (await conn()).transaction(ROWS, "readwrite");
      const done = transactionDone(tx);
      tx.objectStore(ROWS).put(withKey(storeId, row));
      await done;
    },

    async clearStore(storeId) {
      assertStore(owner, storeId);
      const tx = (await conn()).transaction([ROWS, META], "readwrite");
      const done = transactionDone(tx);
      tx.objectStore(ROWS).clear();
      tx.objectStore(META).clear();
      await done;
    },

    close() {
      closed = true;
      const pending = connection;
      connection = null;
      pending?.then((db) => db?.close()).catch(() => {});
    },
  };
};

// ---------------------------------------------------------------------------
// เปิด / ลบ / รายชื่อ
// ---------------------------------------------------------------------------

const opened = new Map<string, Promise<StockDb>>();

/** database ของร้านนี้ (เปิดครั้งเดียวต่อร้าน)  เปิด IndexedDB ไม่ได้ = memory */
export const getStockDb = (storeId: string): Promise<StockDb> => {
  if (!storeId) return Promise.reject(new Error("stock db: ไม่มี storeId"));
  let db = opened.get(storeId);
  if (!db) {
    db = openIndexedDb(storeId).then((idb) => {
      if (idb) return createIdbDb(storeId, idb);
      console.warn("[stock] เปิด IndexedDB ไม่ได้ ใช้หน่วยความจำแทน (ต้องโหลดใหม่ทุกครั้งที่เปิดแอป)");
      return createMemoryDb(storeId);
    });
    opened.set(storeId, db);
  }
  return db;
};

/** ลบ database ของร้านนี้ทั้งก้อน (รอไม่เกิน ~3 วิ ถ้าแท็บเก่าไม่ยอมปิด IDB จะลบให้ทีหลังเอง) */
export const deleteStockDb = async (storeId: string): Promise<void> => {
  const db = opened.get(storeId);
  opened.delete(storeId);
  if (db) {
    try {
      (await db).close();
    } catch {}
  }
  if (!hasIndexedDb()) return;
  await new Promise<void>((resolve) => {
    const timer = setTimeout(resolve, 3000);
    const done = () => {
      clearTimeout(timer);
      resolve();
    };
    try {
      const request = indexedDB.deleteDatabase(stockDbName(storeId));
      request.onsuccess = done;
      request.onerror = done;
    } catch {
      done();
    }
  });
};

/** ร้านที่มี database แคชสต๊อกอยู่ในเครื่อง (ถ้าเบราว์เซอร์บอกได้) */
export const listStockDbStoreIds = async (): Promise<string[]> => {
  try {
    if (!hasIndexedDb() || typeof (indexedDB as any).databases !== "function") return [];
    const list: Array<{ name?: string }> = await (indexedDB as any).databases();
    return list
      .map((info) => info.name || "")
      .filter((name) => name.startsWith(DB_PREFIX))
      .map((name) => name.slice(DB_PREFIX.length))
      .filter(Boolean);
  } catch {
    return [];
  }
};
